import { ShoppingBag, Mail, MessageCircle, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { EBAY, EMAIL, PHONE, waLink } from "../../lib/api";
import { Eyebrow, Reveal } from "../site/Reveal";
import { EnquiryForm } from "../site/EnquiryForm";

const STEPS = [
  ["Identify", "Find the part number on your original airbag, or search by make, model and position."],
  ["Check fit", "Compare the part number and photos. Not sure? Send us your reg on WhatsApp."],
  ["Order", "Pay securely online by card, or order directly by phone or WhatsApp."],
  ["Fit safely", "Have the part fitted and coded by a qualified technician, or get in touch about our fitting service."],
];

export const HowItWorks = () => (
  <section data-testid="how-it-works-section" className="border-y border-white/10 bg-[#0B1016]">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12">
      <Reveal><Eyebrow>How it works</Eyebrow>
        <h2 className="mt-4 max-w-3xl font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-7xl">Four steps to the right part.</h2>
      </Reveal>
      <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.1} className="group h-full bg-[#0B1016] p-8 transition-colors duration-500 hover:bg-[#121820]">
            <span className="font-display text-7xl font-black text-white/10 transition-colors duration-500 group-hover:text-[#E11D48]">0{i + 1}</span>
            <h3 className="mt-6 font-display text-3xl font-bold uppercase">{t}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">{d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Trade = () => (
  <section id="trade" data-testid="trade-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
    <div className="grid gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-7">
        <Eyebrow>Trade</Eyebrow>
        <h2 className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-7xl">Garages &amp; body shops, <span className="text-outline">talk to us direct.</span></h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
          Repeat orders, bulk enquiries and fitment checks for trade buyers. Send a list of part numbers and we'll come back to you.
        </p>
      </Reveal>
      <Reveal delay={0.15} className="flex flex-col justify-end gap-3 lg:col-span-5">
        <a data-testid="trade-whatsapp-button" href={waLink("Hi CBLS AIRBAGS, I'm a trade buyer and would like to enquire about parts.")} target="_blank" rel="noreferrer" className="group flex items-center justify-between border border-white/10 bg-[#121820] p-6 transition-colors hover:border-emerald-400">
          <span className="flex items-center gap-4"><MessageCircle className="h-5 w-5 text-emerald-400" /><span className="font-display text-2xl font-bold uppercase">WhatsApp</span></span>
          <span className="font-mono text-sm text-slate-300">{PHONE}</span>
        </a>
        <a data-testid="trade-phone-button" href={`tel:${PHONE}`} className="flex items-center justify-between border border-white/10 bg-[#121820] p-6 transition-colors hover:border-[#E11D48]">
          <span className="flex items-center gap-4"><Phone className="h-5 w-5 text-[#E11D48]" /><span className="font-display text-2xl font-bold uppercase">Call</span></span>
          <span className="font-mono text-sm text-slate-300">{PHONE}</span>
        </a>
        <a data-testid="trade-email-button" href={`mailto:${EMAIL}`} className="flex flex-wrap items-center justify-between gap-2 border border-white/10 bg-[#121820] p-6 transition-colors hover:border-[#F59E0B]">
          <span className="flex items-center gap-4"><Mail className="h-5 w-5 text-[#F59E0B]" /><span className="font-display text-2xl font-bold uppercase">Email</span></span>
          <span className="break-all font-mono text-xs text-slate-300">{EMAIL}</span>
        </a>
        <a data-testid="trade-ebay-button" href={EBAY} target="_blank" rel="noreferrer" className="flex items-center justify-between border border-[#F59E0B]/40 bg-[#F59E0B]/[0.06] p-6 transition-colors hover:border-[#F59E0B]">
          <span className="flex items-center gap-4"><ShoppingBag className="h-5 w-5 text-[#F59E0B]" /><span className="font-display text-2xl font-bold uppercase">Our eBay shop</span></span>
          <span className="font-mono text-sm text-slate-300">ebay.co.uk/str/cbls ↗</span>
        </a>
      </Reveal>
    </div>
  </section>
);

const FAQS = [
  ["How do I check an airbag fits my car?", "Match the part number on your original airbag with the one in our listing. If you can't find it, send us your registration, make, model and year on WhatsApp or through the form below and we'll check for you."],
  ["Can I fit an airbag myself?", "Airbags are safety-critical parts. We strongly recommend having them fitted and coded by a qualified technician. We also offer a fitting service covering the West Midlands area — call or WhatsApp us on " + PHONE + " for a quote."],
  ["How do I pay?", "Pay securely online by card, Apple Pay, PayPal or Klarna at checkout, or call or WhatsApp us on " + PHONE + " to order directly. You can also buy from our eBay shop."],
  ["How much is delivery?", "Seat belts, airbags and other parts: Standard delivery £4.99 (2-3 working days) or Express delivery £14.99 (dispatched after 1 working day, arrives in 1-2 days). Dashboards: £70 delivery, dispatched after 1 working day, arrives in 2-3 working days."],
  ["Can I return an item?", "Yes, you can return an item within 14 days. The item must be returned in the same condition it was sent. Terms & conditions: any tampering with an airbag will void the warranty."],
];

export const Faq = () => (
  <section id="faq" data-testid="faq-section" className="border-t border-white/10">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-8 lg:grid-cols-12 lg:px-12">
      <Reveal className="lg:col-span-4"><Eyebrow>FAQ</Eyebrow>
        <h2 className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl">Good questions.</h2>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-8">
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`} className="border-white/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-display text-2xl font-bold uppercase hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="pb-6 text-base leading-relaxed text-slate-300">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="bg-[#E11D48] text-white">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-8 lg:grid-cols-12 lg:px-12">
      <Reveal className="lg:col-span-5">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">Enquiries</p>
        <h2 className="mt-4 font-display text-6xl font-black uppercase leading-[0.85] tracking-tight sm:text-8xl">Can't see it? Ask.</h2>
        <p className="mt-6 max-w-md text-base text-white/90">Tell us your vehicle and the part you need. We'll check stock and fitment and get back to you.</p>
        <a href={`tel:${PHONE}`} data-testid="contact-phone-link" className="mt-8 block font-display text-4xl font-bold">{PHONE}</a>
        <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="mt-2 block break-all font-mono text-sm text-white/90 underline-offset-4 hover:underline">{EMAIL}</a>
      </Reveal>
      <Reveal delay={0.1} className="bg-[#090D12] p-6 sm:p-10 lg:col-span-7">
        <EnquiryForm />
      </Reveal>
    </div>
  </section>
);
